<template>
  <div class="estimated-invoice-container">
    <!-- Watermark for print and screen -->
    <div class="watermark-container" v-if="showWatermark">
      <div class="watermark-text">ESTIMATED INVOICE NOT PAID</div>
    </div>

    <!-- Red Banner Header (visible on screen and print) -->
    <div class="estimated-banner" v-if="showBanner">
      <span>⚠️</span>
      ESTIMATED INVOICE - NOT PAID - FOR PREVIEW ONLY
      <span>⚠️</span>
    </div>

    <!-- Print Controls -->
    <div class="print-controls hidden-print">
      <b-card-text class="d-flex flex-wrap" style="gap: 5px;">
        <b-button size="sm" variant="outline-dark" class="hidden-print" @click="printInvoice" style="width: 60px;">PRINT</b-button>
        <b-button size="sm" variant="outline-secondary" class="hidden-print" @click="closePreview">CLOSE</b-button>
      </b-card-text>
    </div>

    <!-- Invoice Content -->
    <div ref="invoiceContent" class="invoice-content">
      <!-- Store/Company Header -->
      <div class="invoice-header">
        <div class="row" style="width: 100%; margin: 0;">
          <div class="col-md-4 col-sm-12">
            <div v-if="locationDetails.short_name && locationDetails.short_name.includes('SEN')">
              <div><h2><b>INT FOURNITURE</b></h2></div>
              <div>
                <div><sub><b>{{ locationDetails.address }}</b></sub></div>
                <div><sub>{{ locationDetails.email }}</sub></div>
                <div><sub>{{ locationDetails.phone }}</sub></div>
              </div>
            </div>
            <div v-else>
              <div><h2><b>Furniture Mecca</b></h2></div>
              <div>
                <div><sub><b>{{ locationDetails.address }}</b></sub></div>
                <div><sub>{{ locationDetails.email }}</sub></div>
                <div><sub>{{ locationDetails.phone }}</sub></div>
                <div><sub>{{ locationDetails.website }}</sub></div>
              </div>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <div>
              <div>
                <div><b>ESTIMATE # {{ invoice.estimate_id || invoice.draft_id || 'N/A' }}</b></div>
              </div>
              <div>
                <div>DATE: {{ formatDate(invoice.created_at) }}</div>
              </div>
              <div>
                <div>PREPARED BY: {{ invoice.created_by || 'N/A' }}</div>
              </div>
              <div>
                <div>LOC: {{ getLocationName(invoice.location_id) }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <!-- Empty column for layout consistency -->
          </div>
        </div>
      </div>

      <!-- Customer Information -->
      <div class="customer-section">
        <h5><b>CUSTOMER INFORMATION</b></h5>
        <div class="customer-card">
          <div class="customer-info-grid">
            <div class="customer-info-item">
              <div class="info-label">FULLNAME</div>
              <div class="info-value"><b>{{ getCustomerName(invoice.customer) }}</b></div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">ADDRESS</div>
              <div class="info-value">{{ getCustomerAddress(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">ADDRESS 2</div>
              <div class="info-value">{{ getCustomerAddress2(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">PHONE # 1</div>
              <div class="info-value">{{ getCustomerPhone1(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">PHONE # 2</div>
              <div class="info-value">{{ getCustomerPhone2(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">EMAIL</div>
              <div class="info-value">{{ getCustomerEmail(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">ZIPCODE</div>
              <div class="info-value">{{ getCustomerZipcode(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">CITY</div>
              <div class="info-value">{{ getCustomerCity(invoice.customer) }}</div>
            </div>
            <div class="customer-info-item">
              <div class="info-label">STATE</div>
              <div class="info-value">{{ getCustomerState(invoice.customer) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Products Section -->
      <div class="products-section">
        <h5><b>PRODUCTS</b></h5>
      </div>

      <!-- Group by Parent Products -->
      <div v-for="(parentGroup, parentSku) in groupedByParent" v-bind:key="parentSku"
           class="product-group">

        <!-- Parent Products Table - Only show when parent data exists -->
        <div v-if="parentGroup[0] && parentGroup[0].parent_product_sku" class="parent-table">
          <table>
            <thead>
              <tr>
                <th class="parent-th">PARENT IMAGE</th>
                <th class="parent-th">PARENT SKU</th>
                <th class="parent-th">PARENT PRODUCT NAME</th>
                <th class="parent-th">QTY</th>
                <th class="parent-th">UNIT PRICE</th>
                <th class="parent-th">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <!-- Parent Product Row -->
              <tr class="parent-row">
                <!-- Parent Image -->
                <td class="parent-td image-cell">
                  <div class="image-wrapper">
                    <div v-if="getParentImage(parentGroup[0])" class="image-container">
                      <img
                        :src="getParentImage(parentGroup[0])"
                        :alt="parentGroup[0].parent_product_name"
                        @error="handleImageError"
                        class="product-image"
                        @click="showFullImage(parentGroup[0], 'parent')"
                      />
                    </div>
                    <div v-else class="no-image">
                      <span>No Image</span>
                    </div>
                  </div>
                </td>

                <!-- Parent SKU -->
                <td class="parent-td">
                  <div class="sku-text">
                    {{ parentSku }}
                  </div>
                </td>

                <!-- Parent Product Name -->
                <td class="parent-td">
                  <div class="product-name">
                    {{ parentGroup[0].parent_product_name }}
                  </div>
                </td>

                <!-- Parent Quantity -->
                <td class="parent-td quantity-cell">
                  <div class="quantity-text">
                    {{ parentGroup[0].parent_product_quantity }}
                  </div>
                </td>

                <!-- Parent Unit Price -->
                <td class="parent-td price-cell">
                  <div class="price-text">
                    ${{ parseFloat(parentGroup[0].parent_product_selling_price || 0).toFixed(2) }}
                  </div>
                </td>

                <!-- Parent Total Price -->
                <td class="parent-td total-cell">
                  <div class="total-text">
                    ${{ calculateParentTotal(parentGroup).toFixed(2) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Child Products Table -->
        <div class="child-table">
          <div class="child-header">
            <h6>📦 PRODUCTS INCLUDED</h6>
          </div>
          <table>
            <thead>
              <tr>
                <th class="child-th">IMAGE</th>
                <th class="child-th">SKU</th>
                <th class="child-th">PRODUCT DESC</th>
                <th class="child-th">QTY</th>
                <th class="child-th">CUBES</th>
                <th class="child-th">UNIT PRICE</th>
                <th class="child-th">UNIT DISCOUNT</th>
                <th class="child-th">AMOUNT</th>
                <th class="child-th">TYPE</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in parentGroup" v-bind:key="idx"
                  :class="['child-row', idx % 2 === 0 ? 'row-even' : 'row-odd']">
                <!-- Child Image -->
                <td class="child-td image-cell">
                  <div class="image-wrapper">
                    <div v-if="getProductImage(item)" class="image-container">
                      <img
                        :src="getProductImage(item)"
                        :alt="item.product_name || item.product_sku"
                        @error="handleImageError"
                        class="product-image"
                        @click="showFullImage(item, 'product')"
                      />
                    </div>
                    <div v-else class="no-image">
                      <span>No Image</span>
                    </div>
                    
                    <!-- Special badges -->
                    <div v-if="item.is_special" class="badge special-badge">
                      SPECIAL ORDER
                    </div>
                    <div v-if="item.as_is" class="badge as-is-badge">
                      AS-IS
                    </div>
                  </div>
                </td>

                <!-- Child SKU -->
                <td class="child-td">
                  <div class="sku-text">
                    {{ (item.product_sku || '').toUpperCase() }}
                  </div>
                  <div v-if="item.is_special" class="special-notice">
                    Special orders might take up to 8 weeks
                  </div>
                </td>

                <!-- Child Product Name -->
                <td class="child-td">
                  <div class="product-name">
                    {{ (item.product_name || item.product_sku).toUpperCase() }}
                    
                    <!-- As-Is notice -->
                    <div v-if="item.as_is === true" class="as-is-notice">
                      ⚠️ SOLD AS-IS
                    </div>
                  </div>
                </td>

                <!-- Child Quantity -->
                <td class="child-td quantity-cell">
                  {{ item.quantity }}
                </td>

                <!-- Child Cubes -->
                <td class="child-td quantity-cell">
                  {{ item.cubes || 0.0 }}
                </td>

                <!-- Child Unit Price -->
                <td class="child-td price-cell">
                  ${{ parseFloat(item.selling_price || 0).toFixed(2) }}
                </td>

                <!-- Child Unit Discount -->
                <td class="child-td price-cell">
                  ${{ parseFloat(item.unit_discount_value || 0).toFixed(2) }}
                </td>

                <!-- Child Amount -->
                <td class="child-td price-cell">
                  ${{ calculateItemTotal(item).toFixed(2) }}
                </td>

                <!-- Child Order Type -->
                <td class="child-td type-cell">
                  {{ formatOrderType(item.order_type) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Charges and Summary -->
      <div class="summary-section">
        <h5><b>PAYMENT SUMMARY (IN DOLLARS)</b></h5>
        <div class="summary-grid">
          <div class="charges-column">
            <div><b>WARRANTY:</b> ${{ formatCurrency(invoice.warranty_charges) }}</div>
            <div><b>SETUP:</b> ${{ formatCurrency(invoice.setup_charges) }}</div>
            <div><b>DELIVERY:</b> ${{ formatCurrency(invoice.delivery_charges) }}</div>
          </div>

          <div class="empty-column">
            &nbsp;
          </div>

          <div class="totals-column">
            <div><b>SUBTOTAL:</b> ${{ formatCurrency(invoice.subtotal) }}</div>
            <div><b>TAXABLE SUBTOTAL:</b> ${{ formatCurrency(invoice.taxable_subtotal) }}</div>
            <div><b>SALES TAX ({{ formatTaxRate(invoice.tax_perc) }}%):</b> ${{ formatCurrency(invoice.sales_tax) }}</div>
            <div><b>GRAND TOTAL:</b> ${{ formatCurrency(invoice.total) }}</div>
            
            <div class="estimated-balance">
              <b>ESTIMATED BALANCE: ${{ calculateBalance().toFixed(2) }}</b>
            </div>
          </div>
        </div>

        <hr />

        <div class="payment-section">
          <div class="payment-row">
            <div v-for="(pm, pmidx) in getPaymentMethods()" v-bind:key="pmidx" class="payment-item">
              <div class="payment-header">PAYMENT # {{ pmidx + 1 }}</div>
              <div>{{ formatPaymentMethodValue(pm.value) }}</div>
              <div>$ {{ parseFloat(pm.amount || 0).toFixed(2) }}</div>
              <div v-if="pm.value == 'cc' && pm.name">LAST 4 # {{ pm.name }}</div>
              <div v-if="pm.value == 'store_credit' && pm.store_credit_invoice_id">INVOICE # {{ pm.store_credit_invoice_id }}</div>
              <div v-if="pm.value == 'finance' && pm.lease_id"># {{ pm.lease_id }}</div>
              <div v-if="pm.value == 'finance' && pm.name">{{ pm.name }}</div>
            </div>
            <div v-if="!hasPaymentMethods()" class="no-payment">
              <div class="alert-info">No payment methods specified (estimated invoice)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Signature Line -->
      <div class="signature-line">
        CUSTOMER SIGNATURE: _____________________________________
      </div>
      
      <div class="invoice-footer">
        <p><strong>THIS IS AN ESTIMATED INVOICE - NOT A BILL</strong></p>
        <p><small>Prices and availability subject to change until order is confirmed</small></p>
      </div>
    </div>
    
    <!-- Image Viewer Modal -->
    <b-modal id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
      <div class="text-center">
        <b-img
          :src="currentFullSizeImage"
          fluid
          alt="Full size product image"
          style="max-height: 70vh; object-fit: contain;"
        />
        <div class="mt-3">
          <strong>{{ currentProductName }}</strong>
        </div>
      </div>
      <template #modal-footer>
        <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-image-viewer')">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'EstimatedInvoicePreview',
  props: {
    invoiceData: {
      type: Object,
      required: true
    },
    locationData: {
      type: Object,
      default: function() {
        return {}
      }
    },
    showWatermark: {
      type: Boolean,
      default: true
    },
    showBanner: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    return {
      invoice: this.invoiceData || {},
      locationDetails: {
        address: '',
        email: '',
        phone: '',
        short_name: '',
        website: 'www.myfurnituremecca.com'
      },
      currentDate: moment().format('MM/DD/YYYY'),
      currentFullSizeImage: null,
      currentProductName: null
    }
  },
  computed: {
    groupedByParent: function() {
      const groups = {}
      if (!this.invoice.items || !this.invoice.items.length) return groups

      for (let i = 0; i < this.invoice.items.length; i++) {
        const item = this.invoice.items[i]
        const parentSku = item.parent_product_sku || item.product_sku
        if (!groups[parentSku]) {
          groups[parentSku] = []
        }
        groups[parentSku].push(item)
      }
      return groups
    }
  },
  created: function() {
    this.initializeLocationDetails()
  },
  methods: {
    initializeLocationDetails: function() {
      if (this.locationData && Object.keys(this.locationData).length > 0) {
        this.locationDetails = Object.assign({}, this.locationDetails, this.locationData)
      } else if (this.invoice.location_id) {
        try {
          const packet = JSON.parse(localStorage.getItem('packet') || '{}')
          const locations = packet.locations || []
          for (let i = 0; i < locations.length; i++) {
            const location = locations[i]
            if (location.value == this.invoice.location_id) {
              this.locationDetails = {
                address: location.address || '',
                email: location.email || '',
                phone: location.phone || '',
                short_name: location.short_name || '',
                website: 'www.myfurnituremecca.com'
              }
              break
            }
          }
        } catch (e) {
          console.error('Error loading location details:', e)
        }
      }
    },

    formatDate: function(date) {
      return date ? moment(date).format('MM/DD/YYYY') : moment().format('MM/DD/YYYY')
    },

    getLocationName: function(locationId) {
      try {
        const packet = JSON.parse(localStorage.getItem('packet') || '{}')
        return (packet.locationsdict && packet.locationsdict[locationId]) || 'N/A'
      } catch (error) {
        return 'N/A'
      }
    },

    getCustomerName: function(customer) {
      return (customer && customer.fullname) || 'N/A'
    },

    getCustomerAddress: function(customer) {
      return (customer && customer.address_line_1) || 'N/A'
    },

    getCustomerAddress2: function(customer) {
      return (customer && customer.address_line_2) || ''
    },

    getCustomerPhone1: function(customer) {
      return (customer && customer.phone) || ''
    },

    getCustomerPhone2: function(customer) {
      return (customer && customer.phone_2) || ''
    },

    getCustomerEmail: function(customer) {
      return (customer && customer.email) || ''
    },

    getCustomerZipcode: function(customer) {
      return (customer && customer.zipcode) || ''
    },

    getCustomerCity: function(customer) {
      return (customer && customer.city) || ''
    },

    getCustomerState: function(customer) {
      return (customer && customer.state) || ''
    },

    getParentImage: function(item) {
      if (!item || !item.parent_image_url) return null
      return this.proxyImageUrl(item.parent_image_url)
    },

    getProductImage: function(item) {
      if (!item || !item.image_url) return null
      return this.proxyImageUrl(item.image_url)
    },

    proxyImageUrl: function(url) {
      if (!url) return null
      if (url.includes('fmapi.myfurnituremecca.com')) {
        return 'https://corsproxy.io/?' + encodeURIComponent(url)
      }
      return url
    },

    handleImageError: function(event) {
      console.warn('Image failed to load:', event.target.src)
      const parent = event.target.parentElement
      if (parent) {
        const existingFallback = parent.querySelector('.no-image-placeholder')
        if (!existingFallback) {
          parent.innerHTML = `
            <div class="no-image-placeholder" style="width: 60px; height: 50px; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 1px dashed #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="font-size: 20px; margin-bottom: 5px;">📷</div>
              <div style="color: #999; font-size: 10px;">No Image</div>
            </div>
          `
        }
      }
      event.target.style.display = 'none'
    },

    showFullImage: function(item, type) {
      if (!item) return

      const imageUrl = type === 'parent' ? this.getParentImage(item) : this.getProductImage(item)
      if (imageUrl) {
        this.$bvModal.show('modal-image-viewer')
        this.$nextTick(function() {
          this.currentFullSizeImage = imageUrl
          this.currentProductName = item.product_name || item.product_sku || 'Product Image'
        })
      }
    },

    calculateParentTotal: function(items) {
      if (!items || !items.length) return 0
      let sum = 0
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        sum += ((item.selling_price * item.quantity) - (item.discount_offered || 0))
      }
      return sum
    },

    calculateItemTotal: function(item) {
      return ((item.selling_price * item.quantity) - (item.discount_offered || 0))
    },

    formatOrderType: function(type) {
      const types = {
        'customer_takes': 'Take Home',
        'store_pickup': 'Store Pickup',
        'home_delivery': 'Home Delivery'
      }
      return types[type] || type || 'N/A'
    },

    formatCurrency: function(value) {
      return parseFloat(value || 0).toFixed(2)
    },

    formatTaxRate: function(rate) {
      return ((rate || 0) * 100).toFixed(2)
    },

    getPaymentMethods: function() {
      return this.invoice.payment_methods || []
    },

    hasPaymentMethods: function() {
      return this.invoice.payment_methods && this.invoice.payment_methods.length > 0
    },

    formatPaymentMethodValue: function(value) {
      return (value || '').toUpperCase()
    },

    getTotalPayments: function() {
      if (!this.invoice.payment_methods || !this.invoice.payment_methods.length) return 0
      let sum = 0
      for (let i = 0; i < this.invoice.payment_methods.length; i++) {
        sum += parseFloat(this.invoice.payment_methods[i].amount || 0)
      }
      return sum
    },

    calculateBalance: function() {
      return (this.invoice.total || 0) - this.getTotalPayments()
    },

    printInvoice: function() {
      window.print()
    },

    closePreview: function() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* ===== RESET & BASE STYLES ===== */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.estimated-invoice-container {
  position: relative;
  min-height: 100vh;
  background: white;
  padding: 20px;
  font-family: Arial, Helvetica, sans-serif;
  color: #333;
}

/* ===== WATERMARK ===== */
.watermark-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 9999;
}

.watermark-text {
  transform: rotate(-30deg);
  font-size: 80px;
  font-weight: bold;
  color: rgba(255, 0, 0, 0.15);
  white-space: nowrap;
  text-transform: uppercase;
  border: 10px solid rgba(255, 0, 0, 0.15);
  padding: 40px 80px;
  border-radius: 20px;
  font-family: Arial, Helvetica, sans-serif;
}

/* ===== ESTIMATED BANNER ===== */
.estimated-banner {
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  color: white;
  padding: 15px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3);
  text-transform: uppercase;
  letter-spacing: 2px;
  position: sticky;
  top: 0;
  z-index: 1000;
}

.estimated-banner span {
  margin: 0 10px;
  font-size: 30px;
  display: inline-block;
  vertical-align: middle;
}

/* ===== PRINT CONTROLS ===== */
.print-controls {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1001;
}

.print-controls .d-flex {
  display: flex;
  gap: 5px;
}

.print-controls .btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.print-controls .btn-outline-dark {
  background: transparent;
  border-color: #343a40;
  color: #343a40;
}

.print-controls .btn-outline-dark:hover {
  background: #343a40;
  color: white;
}

.print-controls .btn-outline-secondary {
  background: transparent;
  border-color: #6c757d;
  color: #6c757d;
}

.print-controls .btn-outline-secondary:hover {
  background: #6c757d;
  color: white;
}

/* ===== INVOICE CONTENT ===== */
.invoice-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0,0,0,0.1);
  margin: 20px auto;
  position: relative;
  z-index: 1;
  max-width: 1200px;
  width: 100%;
}

/* ===== INVOICE HEADER ===== */
.invoice-header {
  margin-top: 20px;
  margin-bottom: 30px;
  width: 100%;
}

.invoice-header .row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
  width: 100%;
}

.invoice-header .col-md-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
  padding: 0 15px;
  box-sizing: border-box;
}

.invoice-header h2 {
  margin: 0 0 10px 0;
  font-size: 24px;
  color: #2c3e50;
}

.invoice-header sub {
  font-size: 13px;
  display: block;
  margin-bottom: 3px;
  color: #555;
}

/* ===== CUSTOMER SECTION ===== */
.customer-section {
  width: 100%;
  margin: 20px 0 30px;
}

.customer-section h5 {
  margin-bottom: 15px;
}

.customer-section h5 b {
  color: #2c3e50;
  border-bottom: 3px solid #3498db;
  padding-bottom: 8px;
  font-weight: bold;
  font-size: 18px;
}

.customer-card {
  width: 100%;
  border: 1px solid #000;
  padding: 20px;
  box-sizing: border-box;
  background: #fafafa;
}

.customer-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.customer-info-item {
  min-width: 150px;
}

.info-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.info-value {
  font-size: 14px;
  word-break: break-word;
  color: #333;
  font-weight: 500;
}

/* ===== PRODUCTS SECTION ===== */
.products-section {
  width: 100%;
  margin: 20px 0 15px;
}

.products-section h5 {
  margin-bottom: 15px;
}

.products-section h5 b {
  color: #2c3e50;
  border-bottom: 3px solid #3498db;
  padding-bottom: 8px;
  font-weight: bold;
  font-size: 18px;
}

.product-group {
  border: 2px solid #000;
  margin-bottom: 20px;
  padding: 15px;
  width: 100%;
  box-sizing: border-box;
  background: white;
  page-break-inside: avoid;
}

/* ===== PARENT TABLE ===== */
.parent-table {
  width: 100%;
  margin-bottom: 20px;
  overflow-x: auto;
}

.parent-table table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Parent table column widths */
.parent-table th:nth-child(1) { width: 15%; }  /* Image */
.parent-table th:nth-child(2) { width: 15%; }  /* SKU */
.parent-table th:nth-child(3) { width: 35%; }  /* Name */
.parent-table th:nth-child(4) { width: 10%; }  /* QTY */
.parent-table th:nth-child(5) { width: 12%; }  /* Unit Price */
.parent-table th:nth-child(6) { width: 13%; }  /* Total */

.parent-table th {
  background: #2c3e50;
  color: white;
  border: 2px solid #2c3e50;
  padding: 10px 8px;
  font-size: 13px;
  font-weight: bold;
  text-align: left;
}

.parent-row {
  background: #f8f9fa;
}

.parent-td {
  border: 1px solid #dee2e6;
  padding: 10px 8px;
  vertical-align: middle;
}

/* ===== CHILD TABLE ===== */
.child-table {
  width: 100%;
  overflow-x: auto;
}

.child-header {
  margin: 0 0 15px 0;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
}

.child-header h6 {
  margin: 0;
  font-size: 14px;
  color: #495057;
  font-weight: bold;
  text-transform: uppercase;
}

.child-table table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Child table column widths */
.child-table th:nth-child(1) { width: 10%; }  /* Image */
.child-table th:nth-child(2) { width: 12%; }  /* SKU */
.child-table th:nth-child(3) { width: 25%; }  /* Description */
.child-table th:nth-child(4) { width: 5%; }   /* QTY */
.child-table th:nth-child(5) { width: 5%; }   /* Cubes */
.child-table th:nth-child(6) { width: 8%; }   /* Unit Price */
.child-table th:nth-child(7) { width: 8%; }   /* Discount */
.child-table th:nth-child(8) { width: 8%; }   /* Amount */
.child-table th:nth-child(9) { width: 5%; }   /* Type */

.child-table th {
  background: white;
  border: 1px solid #000;
  padding: 8px 6px;
  font-size: 12px;
  font-weight: bold;
  text-align: left;
  background: #f1f3f5;
}

.child-row {
  border: 1px solid #dee2e6;
}

.row-even {
  background: white;
}

.row-odd {
  background: #f8f9fa;
}

.child-td {
  border: 1px solid #dee2e6;
  padding: 8px 6px;
  vertical-align: middle;
  font-size: 12px;
}

/* ===== TABLE CELL STYLES ===== */
.image-cell {
  text-align: center;
  width: 120px;
  max-width: 120px;
}

.image-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70px;
  position: relative;
}

.image-container {
  width: 80px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #eee;
  border-radius: 4px;
}

.product-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.no-image {
  width: 80px;
  height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ddd;
  border-radius: 4px;
  background: #f9f9f9;
  color: #999;
  font-size: 10px;
  text-align: center;
}

.no-image span {
  display: block;
}

.badge {
  margin-top: 5px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: bold;
  display: inline-block;
  text-transform: uppercase;
}

.special-badge {
  background: #007bff;
  color: white;
}

.as-is-badge {
  background: #dc3545;
  color: white;
}

.special-notice {
  font-size: 9px;
  color: #856404;
  background: #fff3cd;
  padding: 2px 4px;
  margin-top: 3px;
  border-radius: 2px;
  border-left: 2px solid #ffc107;
}

.as-is-notice {
  color: #d32f2f;
  font-weight: bold;
  font-size: 10px;
  margin-top: 3px;
  padding: 2px 0;
}

.sku-text {
  font-size: 12px;
  font-weight: 500;
  word-break: break-word;
  color: #2c3e50;
}

.product-name {
  font-size: 12px;
  font-weight: 500;
  word-break: break-word;
  color: #333;
  line-height: 1.3;
}

.quantity-cell {
  text-align: center;
  font-weight: 500;
}

.price-cell {
  text-align: right;
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

.total-cell {
  text-align: right;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

.type-cell {
  text-align: center;
  font-size: 11px;
}

.quantity-text, .price-text, .total-text {
  font-size: 12px;
  font-weight: 500;
}

/* ===== SUMMARY SECTION ===== */
.summary-section {
  margin-top: 30px;
  width: 100%;
  padding: 25px;
  border: 1px solid #dee2e6;
  box-sizing: border-box;
  background: #fcfcfc;
  page-break-inside: avoid;
}

.summary-section h5 {
  margin-bottom: 20px;
}

.summary-section h5 b {
  color: #2c3e50;
  border-bottom: 3px solid #3498db;
  padding-bottom: 8px;
  font-weight: bold;
  font-size: 18px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.charges-column {
  text-align: left;
  font-size: 14px;
}

.charges-column div {
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px dotted #ddd;
}

.totals-column {
  text-align: right;
  font-size: 14px;
}

.totals-column div {
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px dotted #ddd;
}

.totals-column div:last-child {
  border-bottom: none;
}

.estimated-balance {
  color: #d32f2f;
  margin-top: 15px;
  padding-top: 10px;
  font-size: 16px;
  font-weight: bold;
  border-top: 2px solid #d32f2f !important;
}

hr {
  margin: 20px 0;
  border: 0;
  border-top: 2px solid #dee2e6;
}

/* ===== PAYMENT SECTION ===== */
.payment-section {
  margin-top: 25px;
}

.payment-row {
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
}

.payment-item {
  flex: 1 1 200px;
  font-size: 13px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.payment-header {
  font-weight: bold;
  margin-bottom: 8px;
  color: #2c3e50;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 4px;
}

.payment-item div {
  margin-bottom: 3px;
}

.no-payment {
  flex: 1;
}

.alert-info {
  padding: 15px;
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  color: #0c5460;
  font-size: 14px;
}

/* ===== SIGNATURE LINE ===== */
.signature-line {
  text-align: right;
  width: 100%;
  margin-top: 50px;
  margin-bottom: 20px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  padding-right: 20px;
}

/* ===== INVOICE FOOTER ===== */
.invoice-footer {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px dashed #d32f2f;
  color: #d32f2f;
}

.invoice-footer p {
  margin: 5px 0;
  font-size: 14px;
}

.invoice-footer small {
  font-size: 11px;
  opacity: 0.9;
}

/* ===== IMAGE MODAL STYLES ===== */
.text-center {
  text-align: center;
}

.mt-3 {
  margin-top: 1rem;
}

/* ===== PRINT STYLES ===== */
@media print {
  @page {
    size: auto;
    margin: 0.5in;
  }
  
  .hidden-print {
    display: none !important;
  }
  
  body {
    background: white;
    font-size: 11pt;
    line-height: 1.3;
  }
  
  .estimated-invoice-container {
    padding: 0;
    background: white;
    height: auto;
    overflow: visible;
  }
  
  .invoice-content {
    margin: 0;
    padding: 0.2in;
    box-shadow: none;
    max-width: 100%;
    width: 100%;
    overflow: visible;
    background: white;
  }
  
  /* Table print fixes */
  table {
    page-break-inside: avoid;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }
  
  th, td {
    border: 1px solid #000 !important;
  }
  
  /* Column widths for print */
  .parent-table th:nth-child(1) { width: 12%; }
  .parent-table th:nth-child(2) { width: 12%; }
  .parent-table th:nth-child(3) { width: 38%; }
  .parent-table th:nth-child(4) { width: 8%; }
  .parent-table th:nth-child(5) { width: 12%; }
  .parent-table th:nth-child(6) { width: 18%; }
  
  .child-table th:nth-child(1) { width: 8%; }
  .child-table th:nth-child(2) { width: 10%; }
  .child-table th:nth-child(3) { width: 28%; }
  .child-table th:nth-child(4) { width: 5%; }
  .child-table th:nth-child(5) { width: 5%; }
  .child-table th:nth-child(6) { width: 9%; }
  .child-table th:nth-child(7) { width: 9%; }
  .child-table th:nth-child(8) { width: 9%; }
  .child-table th:nth-child(9) { width: 5%; }
  
  /* Image cell print */
  .image-cell {
    width: 0.6in !important;
    max-width: 0.6in !important;
  }
  
  .image-container {
    width: 0.5in !important;
    height: 0.4in !important;
    border: 1px solid #ddd !important;
  }
  
  .product-image {
    max-width: 0.45in !important;
    max-height: 0.35in !important;
  }
  
  .no-image {
    width: 0.5in !important;
    height: 0.4in !important;
    font-size: 7pt !important;
  }
  
  /* Ensure colors print */
  .estimated-banner {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    background: #d32f2f !important;
    color: white !important;
    position: relative;
    top: 0;
    margin-bottom: 0.2in;
    border-radius: 0;
  }
  
  .parent-table th {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    background: #2c3e50 !important;
    color: white !important;
    border: 2px solid #2c3e50 !important;
  }
  
  .watermark-container {
    position: fixed !important;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    pointer-events: none;
    display: flex !important;
  }
  
  .watermark-text {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color: rgba(255, 0, 0, 0.15) !important;
    border-color: rgba(255, 0, 0, 0.15) !important;
    font-size: 60px !important;
    padding: 20px 40px !important;
  }
  
  /* Prevent page breaks */
  .product-group,
  .summary-section,
  .customer-section,
  .invoice-header {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  
  /* Ensure borders print */
  .customer-card {
    border: 1px solid black !important;
  }
  
  .product-group {
    border: 2px solid black !important;
  }
  
  /* Text sizes for print */
  body, p, div, span, td, th {
    font-size: 10pt;
  }
  
  .info-label { font-size: 8pt; }
  .info-value { font-size: 10pt; }
  .sku-text { font-size: 9pt; }
  .product-name { font-size: 9pt; }
  .price-text, .total-text { font-size: 9pt; }
  
  /* Remove backgrounds */
  .row-even, .row-odd {
    background: white !important;
  }
  
  .child-table th {
    background: #f0f0f0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Fix overflow */
  .invoice-content {
    overflow: visible !important;
  }
  
  .parent-table, .child-table {
    overflow: visible !important;
  }
}

/* ===== MOBILE RESPONSIVE ===== */
@media (max-width: 768px) {
  .watermark-text {
    font-size: 40px;
    padding: 20px 40px;
    transform: rotate(-20deg);
    border-width: 5px;
  }
  
  .estimated-banner {
    font-size: 16px;
    padding: 10px;
    letter-spacing: 1px;
  }
  
  .estimated-banner span {
    font-size: 20px;
    margin: 0 5px;
  }
  
  .print-controls {
    position: fixed;
    bottom: 10px;
    right: 10px;
    left: 10px;
  }
  
  .print-controls .d-flex {
    display: flex;
    width: 100%;
    gap: 5px;
  }
  
  .print-controls .btn {
    flex: 1;
    font-size: 12px;
    padding: 8px 0;
  }
  
  .invoice-content {
    padding: 15px;
    margin: 10px;
  }
  
  .invoice-header .col-md-4 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 15px;
  }
  
  .customer-info-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .customer-info-item {
    width: 100%;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .totals-column {
    text-align: left;
  }
  
  .payment-row {
    flex-direction: column;
    gap: 15px;
  }
  
  .payment-item {
    width: 100%;
  }
  
  /* Make tables scrollable on mobile */
  .parent-table, .child-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .parent-table table, .child-table table {
    min-width: 800px;
  }
  
  .signature-line {
    text-align: left;
    padding-right: 0;
    font-size: 12px;
  }
}

/* ===== UTILITY CLASSES ===== */
.d-flex {
  display: flex;
}

.flex-wrap {
  flex-wrap: wrap;
}

.hidden-print {
  display: block;
}

/* ===== BOOTSTRAD OVERRIDES ===== */
.b-card-text {
  margin: 0;
}

/* Fix for Bootstrap modal */
.modal-content {
  border-radius: 8px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
}

.modal-title {
  font-weight: 600;
}

.modal-body {
  padding: 1rem;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem;
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/EstimatedInvoicePreview.vue